import AddchartIcon from '@mui/icons-material/Addchart';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartAnalyticsTable, DropdownStyled, TooltipStyled } from 'src/components';
import { useGetAllActiveAndRemovedUsers, useGetAnalytics, useGetVendorList } from 'src/hooks';
import {
  orgCartAnalyticsEndDateAtom,
  orgCartAnalyticsSelectedRequestersAtom,
  orgCartAnalyticsSelectedVendorsAtom,
  orgCartAnalyticsStartDateAtom,
} from 'src/store';
import { exportOrgAnalyticsToExcel } from 'src/utils/exportToExcel/exportOrgAnalyticsToExcel';

export const Analytics = () => {
  const [startDate, setStartDate] = useAtom(orgCartAnalyticsStartDateAtom);
  const [endDate, setEndDate] = useAtom(orgCartAnalyticsEndDateAtom);
  const [selectedRequesters, setSelectedRequesters] = useAtom(
    orgCartAnalyticsSelectedRequestersAtom,
  );
  const [selectedVendors, setSelectedVendors] = useAtom(orgCartAnalyticsSelectedVendorsAtom);
  const [vendorSearch, setVendorSearch] = useState<string>('');

  const usersQuery = useGetAllActiveAndRemovedUsers();
  const vendorListQuery = useGetVendorList();
  const analyticsQuery = useGetAnalytics();

  const { t } = useTranslation();

  const vendorDisplayMemo = useMemo(() => {
    let numDisplayed = vendorListQuery.data?.length || 0;

    if (!vendorListQuery.data) {
      return {
        vendors: [],
        numDisplayed: 0,
      };
    }

    if (!vendorSearch.trim()) {
      return {
        vendors: vendorListQuery.data
          .map((vendor) => {
            const selected = selectedVendors.includes(vendor.vendorId);

            return {
              ...vendor,
              display: true,
              selected,
            };
          })
          .sort((a, b) => (a.selected > b.selected ? -1 : 1)),
        numDisplayed,
      };
    }

    return {
      vendors: vendorListQuery.data
        .map((vendor) => {
          const display = vendor.vendorDisplayName
            .toLowerCase()
            .includes(vendorSearch.toLowerCase());

          const selected = selectedVendors.includes(vendor.vendorId);

          if (!display) {
            numDisplayed--;
          }

          return {
            ...vendor,
            display,
            selected,
          };
        })
        .sort((a, b) => (a.selected > b.selected ? -1 : 1)),
      numDisplayed,
    };
  }, [vendorListQuery.data, vendorSearch, selectedVendors]);

  useEffect(() => {
    if (vendorListQuery.data) {
      setSelectedVendors(vendorListQuery.data.map((vendor) => vendor.vendorId));
    }
  }, [vendorListQuery.data, setSelectedVendors]);

  useEffect(() => {
    if (usersQuery.data) {
      setSelectedRequesters(usersQuery.data.map((user) => user.id));
    }
  }, [usersQuery.data, setSelectedRequesters]);

  const handleClickRequester = (e: React.MouseEvent) => {
    const userId = (e.target as HTMLButtonElement).value as string;

    setSelectedRequesters((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const handleClickVendor = (e: React.MouseEvent) => {
    const vendorId = (e.target as HTMLButtonElement).value as string;

    setSelectedVendors((prev) =>
      prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId],
    );
  };

  const handleSelectAll = (type: 'requesters' | 'vendors') => {
    if (type === 'requesters') {
      setSelectedRequesters(usersQuery.data?.map((user) => user.id) || []);
    } else {
      setSelectedVendors(vendorListQuery.data?.map((vendor) => vendor.vendorId) || []);
    }
  };

  const handleClear = (type: 'requesters' | 'vendors') => {
    if (type === 'requesters') {
      setSelectedRequesters([]);
    } else {
      setSelectedVendors([]);
    }
  };

  return (
    <>
      <section className='flex w-full flex-col items-center justify-center gap-4 bg-offWhite p-6 pt-0 font-secondary xs:pt-2 sm:pt-6 md:px-6 lg:px-8 xl:px-12'>
        <div className='flex w-full flex-row flex-wrap items-center justify-center gap-4'>
          <div className='flex flex-row items-center gap-2'>
            <label className='whitespace-nowrap font-secondary font-semibold text-primary-dark'>
              On or After:
            </label>
            <input
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className='flex h-10 min-w-fit cursor-pointer items-center justify-between rounded bg-cloudGray px-3 py-1 font-secondary font-semibold text-primary-dark transition-all hover:bg-cloudGray/80'
            />
          </div>
          <div className='flex flex-row items-center gap-2'>
            <label className='whitespace-nowrap font-secondary font-semibold text-primary-dark'>
              On or Before:
            </label>
            <input
              type='date'
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className='flex h-10 min-w-fit cursor-pointer items-center justify-between rounded bg-cloudGray px-3 py-1 font-secondary font-semibold text-primary-dark transition-all hover:bg-cloudGray/80'
            />
          </div>
          <DropdownStyled
            darkStyle={false}
            buttonContent={
              <span className='min-w-[140px] whitespace-nowrap font-secondary text-base font-semibold text-primary-dark'>
                Requesters (
                {selectedRequesters.length === usersQuery.data?.length
                  ? 'All'
                  : selectedRequesters.length}
                )
              </span>
            }
          >
            {(closeDropdown) =>
              usersQuery.isLoading ? (
                ''
              ) : (
                <div className='z-20 flex min-w-[200px] max-w-min flex-col items-start justify-center rounded-lg bg-cloudGray font-secondary xs:min-w-[240px]'>
                  <div className='flex w-full flex-col items-start gap-2 py-4 pl-6 pr-4'>
                    <div className='flex w-full flex-row flex-nowrap items-center justify-between'>
                      <button
                        type='button'
                        className='font-secondary text-sm italic text-primary-dark transition-all active:scale-95 disabled:scale-100 disabled:text-primary-dark/50'
                        onClick={() => handleSelectAll('requesters')}
                        disabled={usersQuery.data?.length === selectedRequesters.length}
                      >
                        Select All
                      </button>
                      <button
                        type='button'
                        className='font-secondary text-sm italic text-primary-dark transition-all active:scale-95 disabled:scale-100 disabled:text-primary-dark/50'
                        onClick={() => handleClear('requesters')}
                        disabled={selectedRequesters.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                    {usersQuery?.data?.map((user) => (
                      <button
                        key={user.id}
                        type='button'
                        className='group pointer-events-auto w-full whitespace-nowrap py-0.5 transition-all'
                        value={user.id}
                        onClick={handleClickRequester}
                      >
                        <div className='pointer-events-none flex w-full flex-row flex-nowrap items-center justify-start gap-2 whitespace-nowrap transition-all group-active:scale-95'>
                          <input
                            type='checkbox'
                            readOnly
                            checked={selectedRequesters.includes(user.id)}
                            className='primary-checkbox'
                          />
                          <div className='flex flex-col items-start justify-center gap-0 transition-all'>
                            <span className='w-full whitespace-nowrap text-left font-secondary text-base font-semibold text-primary transition-all group-hover:text-primary/70'>
                              {user.email}
                            </span>
                            <span className='w-full whitespace-nowrap text-left font-secondary text-sm font-normal leading-3 text-primary-dark transition-all group-hover:text-primary-dark/70'>
                              {user.name || ''}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )
            }
          </DropdownStyled>
          <DropdownStyled
            darkStyle={false}
            buttonContent={
              <>
                <span className='min-w-[120px] whitespace-nowrap font-secondary text-base font-semibold text-primary-dark'>
                  Vendors (
                  {selectedVendors.length === vendorListQuery.data?.length
                    ? 'All'
                    : selectedVendors.length}
                  )
                </span>
              </>
            }
            enforceMaxHeight={true}
            enableScrollY={true}
          >
            {(closeDropdown) =>
              !vendorListQuery.isLoading &&
              vendorListQuery.data &&
              vendorListQuery.data.length > 0 && (
                <div className='z-20 flex min-w-[300px] max-w-[300px] flex-col items-start justify-center overflow-y-scroll rounded-lg bg-cloudGray font-secondary xs:min-w-[340px] xs:max-w-[340px]'>
                  <div className='flex w-full flex-col items-start gap-2 py-4 pl-6 pr-4'>
                    <input
                      type='text'
                      placeholder='Search Vendors'
                      value={vendorSearch}
                      onChange={(e) => setVendorSearch(e.target.value)}
                      className='w-full rounded border-transparent bg-offWhite px-3 py-1 font-secondary text-primary-dark transition-all hover:bg-cloudGray/80'
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && vendorSearch.length > 0) {
                          if (vendorDisplayMemo.numDisplayed === 1) {
                            const singleVendorDisplayed = vendorDisplayMemo.vendors.find(
                              (vendor) => vendor.display,
                            );

                            if (singleVendorDisplayed) {
                              setSelectedVendors((prev) =>
                                prev.includes(singleVendorDisplayed.vendorId)
                                  ? prev.filter((id) => id !== singleVendorDisplayed.vendorId)
                                  : [...prev, singleVendorDisplayed.vendorId],
                              );

                              setVendorSearch('');
                            }
                          }
                        }
                      }}
                    />
                    <div className='flex w-full flex-row flex-nowrap items-center justify-between'>
                      <button
                        type='button'
                        className='font-secondary text-sm italic text-primary-dark transition-all active:scale-95 disabled:scale-100 disabled:text-primary-dark/50'
                        onClick={() => handleSelectAll('vendors')}
                        disabled={vendorListQuery.data?.length === selectedVendors.length}
                      >
                        Select All
                      </button>
                      <button
                        type='button'
                        className='font-secondary text-sm italic text-primary-dark transition-all active:scale-95 disabled:scale-100 disabled:text-primary-dark/50'
                        onClick={() => handleClear('vendors')}
                        disabled={selectedVendors.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                    {vendorDisplayMemo.numDisplayed > 0 ? (
                      vendorDisplayMemo.vendors.map((vendor) => (
                        <button
                          key={vendor.vendorId}
                          type='button'
                          className={`${vendor.display ? 'block' : 'hidden'} group pointer-events-auto w-full whitespace-nowrap py-0.5 transition-all`}
                          value={vendor.vendorId}
                          onClick={handleClickVendor}
                        >
                          <div className='pointer-events-none flex w-full flex-row flex-nowrap items-center justify-start gap-2 whitespace-nowrap transition-all group-active:scale-95'>
                            <input
                              type='checkbox'
                              readOnly
                              checked={selectedVendors.includes(vendor.vendorId)}
                              className='primary-checkbox'
                            />
                            <span className='w-full whitespace-pre-wrap text-left font-secondary text-base font-semibold text-primary transition-all group-hover:text-primary/70'>
                              {vendor.vendorDisplayName}
                            </span>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className='text-200 text-pretty py-4 text-center text-rose-500'>
                        No vendors found. Try refining your search.
                      </div>
                    )}
                  </div>
                </div>
              )
            }
          </DropdownStyled>
          <button onClick={() => analyticsQuery.refetch()} className='button-primary h-10'>
            Get Analytics
          </button>
          <TooltipStyled title={'Export Analytics to Excel'}>
            <span>
              <button
                className='group inline-flex flex-nowrap items-center justify-center gap-1.5 rounded-full bg-primary-off py-2.5 pl-[18px] pr-5 font-semibold transition-all active:scale-95 hover:bg-primary-offHover disabled:bg-cloudGray disabled:hover:bg-cloudGray'
                disabled={!analyticsQuery.data || analyticsQuery.data.totalCarts === 0}
                onClick={() =>
                  exportOrgAnalyticsToExcel(
                    analyticsQuery.data || null,
                    startDate,
                    endDate,
                    selectedRequesters.length === usersQuery.data?.length
                      ? 'All'
                      : selectedRequesters,
                    selectedVendors.length === vendorListQuery.data?.length
                      ? 'All'
                      : selectedVendors,
                  )
                }
              >
                <AddchartIcon
                  className='p-0.5 text-2xl text-skyBlue group-disabled:text-blueGray'
                  style={{ fontSize: '24px' }}
                />
                <span className='text-cloudGray group-disabled:text-blueGray'>{t('common.export')}</span>
              </button>
            </span>
          </TooltipStyled>
        </div>
      </section>

      <section className='flex w-full flex-col items-center justify-center gap-8'>
        <CartAnalyticsTable />
      </section>
    </>
  );
};
